import SaveIcon from '@mui/icons-material/Save';
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from '@mui/material/styles';
import LoadingButton from 'common/components/loading-button';
import Guid from 'common/values/guid/guid';
import Individual from 'marketplace/entities/individual/individual';
import MarketplaceIndividualPendingInvitation from 'marketplace/entities/marketplace-individual-pending-invitation/marketplace-individual-pending-invitation';
import MarketplaceTeamAPIService from 'marketplace/entities/marketplace-team/api/marketplace-team-api-service';
import MarketplaceTeam from 'marketplace/entities/marketplace-team/marketplace-team';
import IndividualProfile from 'marketplace/values/individual-profile/individual-profile';
import TeamProfile from 'marketplace/values/team-profile/team-profile';
import EditTeam, { TeamMemberInvite } from 'marketplace/values/team-profile/view/edit-team';
import TeamProfileNameInput from 'marketplace/view/profile-name-input';
import AvatarUpload, { AvatarType } from 'marketplace/view/avatar-upload';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { NavigateFunction } from 'react-router';
import { SetURLSearchParams } from 'react-router-dom';
import { useSession } from 'users/session/session-context';
import * as Constants from 'common/helpers/constants';
import ProfileCategoriesSelect from 'marketplace/view/profile-categories-select';
import Loader from 'common/components/loader';

const MainContainer = styled('div')(() => ({
  display: 'grid',
  gridTemplateRows: 'repeat(5, auto)',
  width: '100%'
}));
const HeaderContainer = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    '& > *': {
      margin: 0,
      padding: theme.spacing(0, 0, 3, 0),
      width: '100%',
    },
    '&:not(last-child)': {
      paddingBottom: theme.spacing(0.5)
    }
  },
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'row',
  gridRow: '1',
  justifyContent: 'center',
  padding: theme.spacing(3, 0, 4, 0),
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: 10,
}));
const AboutYou = styled('section')(({ theme }) => ({
  gridRow: '3',
  marginTop: theme.spacing(1)
}));
const AboutYouTextField = styled(TextField)(() => ({
  width: '100%'
}));
const AvatarNameContainer = styled('div')(() => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center'
}));
const RegisterToMarketplaceCheckbox = styled(FormControlLabel)(() => ({
  whiteSpace: 'nowrap'
}));
const TeamDetails = styled('section')(({ theme }) => ({
  gridRow: '4',
  marginTop: theme.spacing(1)
}));
const EditTeamMembers = styled(EditTeam)(() => ({
  gridRow: '2'
}));
const ButtonContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  bottom: 0,
  gridRow: '5',
  justifySelf: 'flex-start',
  padding: theme.spacing(2, 0),
  position: 'sticky',
  width: '100%',
  zIndex: 10
}));

export type EditTeamProfileProps = {
  isVisible?: boolean;
  teamId?: Guid;
  profile?: TeamProfile;
  navigate: NavigateFunction;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  onFormDirty?: (isDirty?: boolean) => void;
  onSave?: (team: MarketplaceTeam) => void;
}

export default function EditTeamProfile(props: Readonly<EditTeamProfileProps>) {
  const {
    profile,
    navigate,
    searchParams,
    setSearchParams,
    onFormDirty,
    onSave
  } = props;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isProfileValidated, setIsProfileValidated] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(props.isVisible ?? false);

  const [teamId, setTeamId] = useState<Guid | undefined>(props.teamId);
  const [team, setTeam] = useState<MarketplaceTeam | undefined>();
  const [originalTeamProfile, setOriginalTeamProfile] = useState<TeamProfile | undefined>(profile);
  const [updatedTeamProfile, setUpdatedTeamProfile] = useState<TeamProfile | undefined>(profile);
  const [avatar, setAvatar] = useState<File | string | undefined | null>();

  const [draftTeamName, setDraftTeamName] = useState<string | null>(null);
  const [formTeamName, setFormTeamName] = useState<string>(profile?.name ?? Constants.defaultProfileName);
  const [formCategories, setFormCategories] = useState<string[]>(profile?.categories ?? []);
  const [formDescription, setFormDescription] = useState<string>(profile?.description ?? '');
  const [formRegisterToMarketplace, setFormRegisterToMarketplace] = useState<boolean>(isVisible ?? true);
  const [originalRegisterToMarketplace, setOriginalRegisterToMarketplace] = useState<boolean>(isVisible ?? true);
  const [selectedTeamLeader, setSelectedTeamLeader] = useState<Individual>();
  const [isLeaderPromoted, setIsLeaderPromoted] = useState<boolean>(false);
  const [existingTeamMembers, setExistingTeamMembers] = useState<Individual[]>([]);
  const [invitedMembers, setInvitedMembers] = useState<TeamMemberInvite[]>([]);
  const [pendingInvites, setPendingInvites] = useState<MarketplaceIndividualPendingInvitation[]>([]);
  const [removedMemberUserIds, setRemovedMemberUserIds] = useState<Guid[]>([]);
  const [profileHash, setProfileHash] = useState<string>("");

  const session = useSession();

  const validateProfile = (profile: TeamProfile) => {
    if (!profile) return;
    const hasCategories: boolean = profile.categories !== undefined && profile.categories.length > 0;
    const hasName: boolean = profile.name.length > 0 && profile.name !== Constants.defaultProfileName;
    setIsProfileValidated(hasName && hasCategories);
    isDirty();
  }

  const isDirty = (): boolean => {
    if (!updatedTeamProfile) {
      onFormDirty?.(false);
      return false;
    }

    // Compare original register to marketplace to updated register to marketplace
    if (formRegisterToMarketplace !== originalRegisterToMarketplace) {
      onFormDirty?.(true);
      return true;
    }

    if (removedMemberUserIds.length > 0) {
      onFormDirty?.(true);
      return true;
    }

    if (invitedMembers.length > 0) {
      onFormDirty?.(true);
      return true;
    }

    // Compare profile hash to updated vendor-team profile hash
    const hash = JSON.stringify(updatedTeamProfile.toJSON());
    const isDirty = hash !== profileHash;
    onFormDirty?.(isDirty);
    return isDirty;
  }

  const loadProfile = useCallback(async () => {
    try {
      setIsLoading(true);

      if (!teamId) throw new Error('Team ID not found');

      const teamService = new MarketplaceTeamAPIService(session);
      const abortController = new AbortController();
      const returnedTeam = await teamService.getTeamById(teamId, abortController);

      if (!returnedTeam) throw new Error('Team not found');
      if (!returnedTeam.profile) throw new Error('Team profile not found');

      setTeam(returnedTeam);
      setOriginalTeamProfile(returnedTeam.profile);
      setUpdatedTeamProfile(returnedTeam.profile);
      setIsVisible(returnedTeam.isVisible);
      setFormTeamName(returnedTeam.profile.name);
      setSelectedTeamLeader(returnedTeam.leader);
      setExistingTeamMembers(returnedTeam.memberships);
      setFormCategories(returnedTeam.profile.categories ?? []);
      setFormDescription(returnedTeam.profile.description ?? '');

      validateProfile(returnedTeam.profile);

      const hash = JSON.stringify(returnedTeam.profile?.toJSON());
      setProfileHash(hash);

      if (returnedTeam.isVisible !== undefined) {
        setFormRegisterToMarketplace(returnedTeam.isVisible);
        setOriginalRegisterToMarketplace(returnedTeam.isVisible);
      }

      if (returnedTeam.profile.avatarId) {
        const returnedAvatar = await teamService.getTeamAvatar(teamId, abortController);
        setAvatar(returnedAvatar);
      } else {
        setAvatar(null);
      }

      const pendingInvites = await teamService.getTeamInvitationsByTeamId(teamId, abortController);
      if (pendingInvites.length > 0) {
        setPendingInvites(pendingInvites);
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to load profile', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [teamId, session]);

  useEffect(() => {
    if (!teamId) {
      return constructNewTeamProfile();
    }
    void loadProfile();
  }, [loadProfile, teamId]);

  function constructNewTeamProfile() {
    if (!session.user?.id) throw new Error("User not found");
    if (session.user?.individualId === undefined) throw new Error("Individual not found");

    setOriginalTeamProfile(new TeamProfile(Constants.defaultProfileName));

    let individualProfile: IndividualProfile | undefined;
    if (session.user.name?.firstName && session.user.name?.lastName) {
      individualProfile = new IndividualProfile(session.user.id, session.user.name.firstName, session.user.name.lastName);
    }
    const leader = new Individual(session.user.individualId, session.user.id, individualProfile);
    setSelectedTeamLeader(leader);
  }

  function constructTeam(): MarketplaceTeam {
    if (!updatedTeamProfile) throw new Error("Updated Team profile not found");
    if (!selectedTeamLeader) throw new Error("Team leader not found");

    return new MarketplaceTeam(selectedTeamLeader, updatedTeamProfile, formRegisterToMarketplace, []);
  }

  async function handleSaveButtonClick() {
    let updatedTeam: MarketplaceTeam | undefined;

    try {
      setIsSaving(true);

      if (teamId)
        updatedTeam = await updateProfile();
      else
        updatedTeam = await createProfile();

      if (!updatedTeam.id) throw new Error('Failed to save profile');

      await removeMembers(updatedTeam.id);
      await inviteMembers(updatedTeam.id);

      if (isLeaderPromoted) {
        await promoteMemberToLeader(updatedTeam.id);
      }

      const teamService = new MarketplaceTeamAPIService(session);
      const abortController = new AbortController();
      const invites = await teamService.getTeamInvitationsByTeamId(updatedTeam.id, abortController);

      setInvitedMembers([]);
      setPendingInvites(invites ?? []);
      setRemovedMemberUserIds([]);

      setTeamId(updatedTeam.id);
      setFormRegisterToMarketplace(updatedTeam.isVisible);
      setOriginalRegisterToMarketplace(updatedTeam.isVisible);
      setProfileHash(JSON.stringify(updatedTeam.profile.toJSON()));

      enqueueSnackbar('Profile changes saved', { variant: 'success' });
      onSave?.(updatedTeam);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to save profile', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }

  async function updateProfile(): Promise<MarketplaceTeam> {
    try {
      if (!isProfileValidated) throw new Error('Profile is not valid');
      if (!team) throw new Error('Team not found');
      if (!updatedTeamProfile) throw new Error('Updated vendor-team profile not found');

      const updatedTeam = constructTeam();
      const teamService = new MarketplaceTeamAPIService(session);
      const teamResponse = await teamService.updateTeam(team, updatedTeam);
      setTeam(teamResponse);
      setOriginalTeamProfile(teamResponse.profile);
      setUpdatedTeamProfile(teamResponse.profile);

      return teamResponse;
    } catch (error) {
      console.error(error);
      throw new Error('Unable to update profile');
    }
  }

  async function createProfile(): Promise<MarketplaceTeam> {
    try {
      if (!isProfileValidated) throw new Error('Profile is not valid');

      const teamToCreate = constructTeam();
      const teamService = new MarketplaceTeamAPIService(session);
      const team = await teamService.createTeam(teamToCreate);

      if (!team.id) throw new Error('Unable to create vendor-team');

      setTeam(team);
      setOriginalTeamProfile(team.profile);
      setUpdatedTeamProfile(team.profile);
      if (avatar) {
        await createAvatar(team.id, avatar as File);
      }

      return team;
    } catch (error) {
      console.error(error);
      throw new Error('Unable to create profile');
    }
  }

  async function createAvatar(teamId: Guid, avatar: File) {
    if (!teamId || !avatar) return;

    try {
      const teamService = new MarketplaceTeamAPIService(session);
      await teamService.createTeamAvatar(teamId, avatar);
    } catch (error) {
      console.error(error);
      throw new Error('Unable to create avatar');
    }
  }

  async function removeMembers(teamId: Guid) {
    for (const userId of removedMemberUserIds) {
      try {
        const pendingInvite = pendingInvites.find(invite => invite.userId.isEqualTo(userId));
        const teamService = new MarketplaceTeamAPIService(session);
        if (pendingInvite) {
          await teamService.cancelTeamInvitation(pendingInvite.invitationId);
          continue;
        }
        await teamService.removeTeamMember(teamId, userId);
      } catch (error) {
        console.error(error);
        throw new Error(`Unable to remove member with userId ${userId}`);
      }
    }
  }

  async function inviteMembers(teamId: Guid) {
    const teamService = new MarketplaceTeamAPIService(session);
    for (const member of invitedMembers) {
      await teamService.createTeamInvitation(member.id, teamId, member.message);
    }
  }

  async function promoteMemberToLeader(teamId: Guid) {
    const memberToPromote = team?.memberships.find(member => member.userId?.isEqualTo(team.leader.userId));
    if (memberToPromote?.userId) {
      const teamService = new MarketplaceTeamAPIService(session);
      await teamService.promoteTeamMember(teamId, memberToPromote);
    }
    setIsLeaderPromoted(false);
  }

  function handleMemberAdded(newMember: TeamMemberInvite) {
    if (invitedMembers.find(member => member.userId?.isEqualTo(newMember.userId))) return;
    setInvitedMembers([...invitedMembers, newMember]);
  }

  function handleMemberUpdated(newMember: TeamMemberInvite) {
    const updatedMembers = invitedMembers.map(member => {
      if (member.userId?.isEqualTo(newMember.userId)) return newMember;
      return member;
    });
    setInvitedMembers(updatedMembers);
  }

  function handleMemberRemoved(userId: Guid) {
    setRemovedMemberUserIds([...removedMemberUserIds, userId]);
  }

  function cloneProfileForUpdate(): TeamProfile | undefined {
    if (!originalTeamProfile) return;
    return updatedTeamProfile ?
      updatedTeamProfile.clone() :
      originalTeamProfile.clone();
  }

  return (
    <MainContainer>
      <HeaderContainer>
        <AvatarNameContainer>
          <AvatarUpload
            accept="image/jpeg, image/png"
            id={teamId}
            type={AvatarType.Team}
            avatar={avatar}
            onUpload={(updatedAvatar) => setAvatar(updatedAvatar)}
            session={session}
          />
          <TeamProfileNameInput
            name={formTeamName}
            draftName={draftTeamName ?? undefined}
            isLoading={isLoading}
            error={formTeamName === Constants.defaultProfileName && teamId && !isLoading}
            onStartEditing={() => setDraftTeamName(formTeamName)}
            onDraftChange={(draft: string) => setDraftTeamName(draft)}
            onCommit={(finalName: string) => {
              setFormTeamName(finalName);

              let updatedProfile = cloneProfileForUpdate();
              if (updatedProfile) {
                updatedProfile = updatedProfile.updateName(finalName);
                setUpdatedTeamProfile(updatedProfile);
                validateProfile(updatedProfile);
              }

              setDraftTeamName(null);
            }}
            onCancel={() => setDraftTeamName(null)}
          />
        </AvatarNameContainer>
        <ProfileCategoriesSelect
          options={Constants.CompanyProfileTypes}
          categories={formCategories}
          onChange={async (updatedCategories: string[]) => {
            setFormCategories(updatedCategories);
            let updatedProfile = cloneProfileForUpdate();
            if (!updatedProfile) return;
            updatedProfile = updatedProfile.updateCategories(updatedCategories);
            setUpdatedTeamProfile(updatedProfile);
            validateProfile(updatedProfile);
          }}
        />
      </HeaderContainer>
      {isLoading && <Loader />}
      {!isLoading && (
        <>
          <EditTeamMembers
            navigate={navigate}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            leader={selectedTeamLeader}
            members={existingTeamMembers}
            invited={invitedMembers}
            pending={pendingInvites}
            removedUserIds={removedMemberUserIds}
            onMemberAdded={handleMemberAdded}
            onMemberUpdated={handleMemberUpdated}
            onMemberRemoved={handleMemberRemoved}
          />
          <AboutYou>
            <Typography variant="h6">
              About Your Team
            </Typography>
            <AboutYouTextField
              value={formDescription}
              onChange={(event) => {
                setFormDescription(event.target.value);
                let updatedProfile = cloneProfileForUpdate();
                if (!updatedProfile) return;
                updatedProfile = updatedProfile.updateDescription(event.target.value);
                setUpdatedTeamProfile(updatedProfile);
                validateProfile(updatedProfile);
              }}
              placeholder='Additional info displayed on your profile'
              variant="outlined"
              multiline
            />
          </AboutYou>
          <TeamDetails>
            <RegisterToMarketplaceCheckbox
              label="Show Team On Marketplace"
              labelPlacement="end"
              control={
                <Checkbox
                  color="primary"
                  checked={formRegisterToMarketplace}
                  onChange={(event) => {
                    setFormRegisterToMarketplace(event.target.checked);
                    onFormDirty?.(true);
                  }}
                />
              }
            />
          </TeamDetails>
        </>
      )}
      <ButtonContainer>
        <LoadingButton
          id="saveButton"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={!isProfileValidated || isSaving}
          loading={isSaving}
          color="primary"
          onClick={handleSaveButtonClick}>
          {teamId ? 'Save Changes' : 'Create Profile'}
        </LoadingButton>
      </ButtonContainer>
    </MainContainer >
  );
}
